// src/pages/components/forms/SingleUserForm.jsx
import React, { useMemo, useState } from "react";
import { createUser } from "../../api/users/user";
import { User, Lock, Mail, Phone, Building2, Users, Briefcase, CheckCircle2 } from "lucide-react";

const RE = {
  username: /^[a-zA-Z0-9._-]{3,20}$/,
  password: /^(?=.*[a-z])(?=.*\d).{6,64}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  phone: /^(01[016789])[-]?\d{3,4}[-]?\d{4}$/,
};
const formatPhoneKR = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};

export default function SingleUserForm({ companies, departments, positions }) {
  const [form, setForm] = useState({
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    company_id: "",
    department_id: "",
    position_id: "",
  });
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  // 의존: 회사 → 부서 → 직위
  const canPickDepartment = !!form.company_id;
  const canPickPosition = !!form.department_id;

  const filteredDepartments = useMemo(() => {
    const cid = Number(form.company_id) || null;
    return departments.filter((d) => (cid ? d.company === cid : false));
  }, [departments, form.company_id]);

  const filteredPositions = useMemo(() => {
    const cid = Number(form.company_id) || null;
    return positions.filter((p) => (cid ? p.company_id === cid : false));
  }, [positions, form.company_id]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    if (
      !form.username ||
      !form.password ||
      !email ||
      !form.phone_number ||
      !form.first_name ||
      !form.last_name ||
      !form.company_id ||
      !form.position_id
    ) {
      setError("필수 항목을 모두 입력해주세요");
      return false;
    }
    if (!RE.username.test(form.username))
      return setError("아이디는 영문/숫자/._- 조합으로 3~20자여야 합니다"), false;
    if (!RE.password.test(form.password))
      return setError("비밀번호는 소문자+숫자 포함 6~64자여야 합니다"), false;
    if (!RE.email.test(email)) return setError("올바른 이메일 형식이 아닙니다"), false;
    if (!RE.phone.test(form.phone_number))
      return setError("올바른 전화번호 형식이 아닙니다"), false;
    setError("");
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setCreating(true);
    try {
      await createUser({
        username: form.username,
        password: form.password,
        email,
        phone_number: form.phone_number,
        first_name: form.first_name,
        last_name: form.last_name,
        membership: {
          company: Number(form.company_id),
          department: form.department_id ? Number(form.department_id) : null,
          position: Number(form.position_id),
          is_primary: true,
        },
      });
      alert("사용자가 성공적으로 추가되었습니다");
      setForm({
        username: "",
        password: "",
        first_name: "",
        last_name: "",
        phone_number: "",
        company_id: "",
        department_id: "",
        position_id: "",
      });
      setEmail("");
    } catch (e) {
      setError(e?.response?.data ? JSON.stringify(e.response.data) : e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* 계정 정보 섹션 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-gray-800">계정 정보</h3>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* Username */}
          <label className="block">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-sm font-medium text-gray-700">아이디</span>
              <span className="text-red-500 text-xs">*</span>
            </div>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm"
                value={form.username}
                onChange={(e) => set("username", e.target.value)}
                placeholder="영문/숫자/._- 3~20자"
              />
            </div>
          </label>

          {/* Password */}
          <label className="block">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-sm font-medium text-gray-700">비밀번호</span>
              <span className="text-red-500 text-xs">*</span>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="password"
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder="소문자+숫자 포함 6~64자"
              />
            </div>
          </label>
        </div>
      </div>

      {/* 개인 정보 섹션 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-green-600" />
            <h3 className="text-sm font-semibold text-gray-800">개인 정보</h3>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* 이름 */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="flex items-center gap-1 mb-2">
                <span className="text-sm font-medium text-gray-700">성</span>
                <span className="text-red-500 text-xs">*</span>
              </div>
              <input
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm"
                value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
                placeholder="홍"
              />
            </label>

            <label className="block">
              <div className="flex items-center gap-1 mb-2">
                <span className="text-sm font-medium text-gray-700">이름</span>
                <span className="text-red-500 text-xs">*</span>
              </div>
              <input
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm"
                value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
                placeholder="길동"
              />
            </label>
          </div>

          {/* Email */}
          <label className="block">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-sm font-medium text-gray-700">이메일</span>
              <span className="text-red-500 text-xs">*</span>
            </div>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
          </label>

          {/* Phone */}
          <label className="block">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-sm font-medium text-gray-700">전화번호</span>
              <span className="text-red-500 text-xs">*</span>
            </div>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm"
                value={form.phone_number}
                onChange={(e) => set("phone_number", formatPhoneKR(e.target.value))}
                placeholder="010-1234-5678"
              />
            </div>
          </label>
        </div>
      </div>

      {/* 조직 정보 섹션 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-purple-600" />
            <h3 className="text-sm font-semibold text-gray-800">조직 정보</h3>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* 회사 */}
          <label className="block">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-sm font-medium text-gray-700">회사</span>
              <span className="text-red-500 text-xs">*</span>
            </div>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm appearance-none bg-white"
                value={form.company_id}
                onChange={(e) => {
                  set("company_id", e.target.value);
                  set("department_id", "");
                  set("position_id", "");
                }}
              >
                <option value="">회사를 선택하세요</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </label>

          {/* 부서 */}
          <label className="block">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-sm font-medium text-gray-700">부서</span>
              <span className="text-xs text-gray-500">(선택)</span>
            </div>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm appearance-none bg-white disabled:bg-gray-50 disabled:text-gray-400"
                value={form.department_id}
                onChange={(e) => {
                  set("department_id", e.target.value);
                  set("position_id", "");
                }}
                disabled={!canPickDepartment}
              >
                <option value="">부서를 선택하세요</option>
                {filteredDepartments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            {!canPickDepartment && (
              <p className="mt-1 text-xs text-gray-500">먼저 회사를 선택해주세요</p>
            )}
          </label>

          {/* 직위 */}
          <label className="block">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-sm font-medium text-gray-700">직위</span>
              <span className="text-red-500 text-xs">*</span>
            </div>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm appearance-none bg-white disabled:bg-gray-50 disabled:text-gray-400"
                value={form.position_id}
                onChange={(e) => set("position_id", e.target.value)}
                disabled={!canPickPosition}
              >
                <option value="">직위를 선택하세요</option>
                {filteredPositions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            {!canPickPosition && (
              <p className="mt-1 text-xs text-gray-500">먼저 부서를 선택해주세요</p>
            )}
          </label>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
            <span className="text-red-600 text-xs font-bold">!</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">입력 오류</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* 제출 버튼 */}
      <button
        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        onClick={submit}
        disabled={creating}
      >
        {creating ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>저장 중...</span>
          </>
        ) : (
          <>
            <CheckCircle2 className="w-5 h-5" />
            <span>사용자 추가</span>
          </>
        )}
      </button>
    </div>
  );
}
